import { useState } from 'react';

import { RunStatus } from '../../../../../../backend/src/types/types';
import { useUpdateRunStatus } from '@/api/mutations/useUpdateRunStatus';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { AllRunStatuses, RunStatusBadgeClasses, RunStatusLabels } from '@/new/lib/pipeline-status';

interface RunStatusCellProps {
  runId: string;
  status: string;
}

/** Status badge plus a platform-operator override, used to unstick runs whose recorded status is wrong. */
export function RunStatusCell({ runId, status }: RunStatusCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(status);
  const { mutate: updateRunStatus, isLoading } = useUpdateRunStatus();

  const handleOpenChange = (open: boolean) => {
    // Start from the latest recorded status, which may have changed since the dialog last closed.
    if (open) setSelectedStatus(status);
    setIsOpen(open);
  };

  const handleConfirm = () => {
    updateRunStatus({ runId, status: selectedStatus as RunStatus }, { onSuccess: () => setIsOpen(false) });
  };

  return (
    <div className="flex items-center gap-0.5">
      <Badge variant="outline" className={RunStatusBadgeClasses[status as RunStatus]}>
        {RunStatusLabels[status as RunStatus] ?? status}
      </Badge>
      <Button variant="ghost" size="iconSm" aria-label="Edit run status" onClick={() => handleOpenChange(true)}>
        <Icon className="lucide--pencil text-xs" />
      </Button>

      <ConfirmationModal
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        title="Edit run status"
        onConfirm={handleConfirm}
        isLoading={isLoading}
        confirmDisabled={selectedStatus === status}
        confirmLabel="Update status"
      >
        <div className="grid gap-4">
          <p className="text-gray-600">
            Records a new status for run <span className="font-mono break-all">{runId}</span>. This does not restart
            processing or undo partial output.
          </p>
          <div className="grid gap-1.5">
            <Label htmlFor={`run-status-${runId}`}>Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger id={`run-status-${runId}`}>
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {AllRunStatuses.map((option) => (
                  <SelectItem key={option} value={option}>
                    {RunStatusLabels[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
}
